import { useState, useMemo, useEffect } from "react";
import { db } from "../../lib/supabase";
import { supabase } from "../../lib/supabase";
import { Plus, Pencil } from "lucide-react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { EvolutionChart } from "../EvolutionChart";
import { TablePagination } from "../ui/table-pagination";

interface MesRendimiento {
  id: string; // UUID
  period_month: string; // ISO date string, e.g. '2024-09-01'
  total_acquisition_cost: number;
  total_asset_value: number;
  annual_rents: number;
  cash: number;
  equivalents: number;
  mortgages: number;
  bullet_loans: number;
  cash_on_cash?: number;
  roi?: number;
  value: number;
  created_at?: string;
  updated_at?: string;
}

const initialMeses: MesRendimiento[] = [
  {
    id: "00000000-0000-0000-0000-000000000001",
    period_month: "2024-01-01",
    value: 0,
    total_acquisition_cost: 0,
    total_asset_value: 0,
    annual_rents: 0,
    cash: 0,
    equivalents: 0,
    mortgages: 0,
    bullet_loans: 0,
    cash_on_cash: 0,
    roi: 0,
  },
  {
    id: "00000000-0000-0000-0000-000000000002",
    period_month: "2024-02-01",
    value: 0,
    total_acquisition_cost: 0,
    total_asset_value: 0,
    annual_rents: 0,
    cash: 0,
    equivalents: 0,
    mortgages: 0,
    bullet_loans: 0,
    cash_on_cash: 0,
    roi: 0,
  },
  {
    id: "00000000-0000-0000-0000-000000000003",
    period_month: "2024-03-01",
    value: 0,
    total_acquisition_cost: 0,
    total_asset_value: 0,
    annual_rents: 0,
    cash: 0,
    equivalents: 0,
    mortgages: 0,
    bullet_loans: 0,
    cash_on_cash: 0,
    roi: 0,
  },
  {
    id: "00000000-0000-0000-0000-000000000004",
    period_month: "2024-04-01",
    value: 0,
    total_acquisition_cost: 0,
    total_asset_value: 0,
    annual_rents: 0,
    cash: 0,
    equivalents: 0,
    mortgages: 0,
    bullet_loans: 0,
    cash_on_cash: 0,
    roi: 0,
  },
  {
    id: "00000000-0000-0000-0000-000000000005",
    period_month: "2024-05-01",
    value: 0,
    total_acquisition_cost: 0,
    total_asset_value: 0,
    annual_rents: 0,
    cash: 0,
    equivalents: 0,
    mortgages: 0,
    bullet_loans: 0,
    cash_on_cash: 0,
    roi: 0,
  },
  {
    id: "00000000-0000-0000-0000-000000000006",
    period_month: "2024-06-01",
    value: 0,
    total_acquisition_cost: 0,
    total_asset_value: 0,
    annual_rents: 0,
    cash: 0,
    equivalents: 0,
    mortgages: 0,
    bullet_loans: 0,
    cash_on_cash: 0,
    roi: 0,
  },
];

export function AdminRendimiento() {
  const [meses, setMeses] = useState<MesRendimiento[]>(initialMeses);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMes, setEditingMes] = useState<MesRendimiento | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newMes, setNewMes] = useState({
    total_acquisition_cost: "",
    total_asset_value: "",
    annual_rents: "",
    cash: "",
    equivalents: "",
    mortgages: "",
    bullet_loans: "",
    period_month: "",
    cash_on_cash: "",
    roi: "",
  });
  const [editMes, setEditMes] = useState({
    total_acquisition_cost: "",
    total_asset_value: "",
    annual_rents: "",
    cash: "",
    equivalents: "",
    mortgages: "",
    bullet_loans: "",
    cash_on_cash: "",
    roi: "",
  });

  const monthNames = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  const formatPeriodMonth = (isoDate: string | undefined) => {
    if (!isoDate) return "";
    // Avoid `new Date(isoDate)` to prevent timezone shifts. Support
    // 'YYYY-MM', 'YYYY-MM-DD' and full timestamps.
    const ym = String(isoDate).slice(0, 7);
    const [yyyy, mm] = ym.split("-");
    const year = Number(yyyy) || NaN;
    const monthIdx = Number(mm) ? Number(mm) - 1 : NaN;
    if (
      !Number.isFinite(year) ||
      !Number.isFinite(monthIdx) ||
      monthIdx < 0 ||
      monthIdx > 11
    )
      return "";
    const month = monthNames[monthIdx] || "";
    return `${month} ${year}`;
  };

  const getNextPeriodMonth = () => {
    const pad = (n: number) => String(n).padStart(2, "0");
    if (meses.length === 0) {
      const now = new Date();
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
    }
    const last = meses[meses.length - 1];
    const ym = String(last.period_month || "").slice(0, 7);
    const [yStr, mStr] = ym.split("-");
    let year = parseInt(yStr, 10);
    let month = parseInt(mStr, 10);
    if (!Number.isFinite(year) || !Number.isFinite(month)) {
      // fallback to now
      const now = new Date();
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
    }
    month = month + 1; // next month
    if (month > 12) {
      month = 1;
      year = year + 1;
    }
    return `${year}-${pad(month)}-01`;
  };

  const nextPeriod = getNextPeriodMonth();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // const formatPercentage = (value: number) => {
  //   return `${value.toFixed(2)}%`;
  // };

  // Calcular el valor automáticamente
  const calculatedValue = useMemo(() => {
    const tac = parseFloat(newMes.total_acquisition_cost) || 0;
    const tav = parseFloat(newMes.total_asset_value) || 0;
    const annualRents = parseFloat(newMes.annual_rents) || 0;
    const cash = parseFloat(newMes.cash) || 0;
    const equivalents = parseFloat(newMes.equivalents) || 0;
    const mortgages = parseFloat(newMes.mortgages) || 0;
    const bulletLoans = parseFloat(newMes.bullet_loans) || 0;

    return (
      (tac + tav) / 2 +
      annualRents +
      cash +
      equivalents -
      mortgages -
      bulletLoans
    );
  }, [
    newMes.total_acquisition_cost,
    newMes.total_asset_value,
    newMes.annual_rents,
    newMes.cash,
    newMes.equivalents,
    newMes.mortgages,
    newMes.bullet_loans,
  ]);

  // Calcular KPIs
  // const latestMes = meses.reduce(
  //   (latest, mes) => (mes.value > 0 && mes.id > latest.id ? mes : latest),
  //   meses[0]
  // );

  // const valuationTotal = latestMes.totalAssetValue;
  // const rentasAnuales = latestMes.annualRents90;
  // const cashDisponible = latestMes.cash + latestMes.equivalents;
  // const totalDebt = latestMes.mortgages + latestMes.bulletLoans;
  // const ltvPortfolio =
  //   valuationTotal > 0 ? (totalDebt / valuationTotal) * 100 : 0;
  // const equityNeto = valuationTotal - totalDebt;

  const handleCreateMes = async () => {
    const payload = {
      total_acquisition_cost: parseFloat(newMes.total_acquisition_cost) || 0,
      total_asset_value: parseFloat(newMes.total_asset_value) || 0,
      annual_rents: parseFloat(newMes.annual_rents) || 0,
      cash: parseFloat(newMes.cash) || 0,
      equivalents: parseFloat(newMes.equivalents) || 0,
      mortgages: parseFloat(newMes.mortgages) || 0,
      bullet_loans: parseFloat(newMes.bullet_loans) || 0,
      cash_on_cash: parseFloat(newMes.cash_on_cash) || 0,
      roi: parseFloat(newMes.roi) || 0,
      value: calculatedValue,
      period_month: newMes.period_month || nextPeriod,
    };

    setCreateLoading(true);
    try {
      await db.createNavMonth(payload);

      // refresh list from server to keep UI consistent
      await fetchNavMonths();
      setIsCreateModalOpen(false);
      setNewMes({
        total_acquisition_cost: "",
        total_asset_value: "",
        annual_rents: "",
        cash: "",
        equivalents: "",
        mortgages: "",
        bullet_loans: "",
        period_month: "",
        cash_on_cash: "",
        roi: "",
      });
    } catch (err: any) {
      // Do NOT perform local-only create. Surface error and keep form state for retry.
      // eslint-disable-next-line no-console
      console.error("createNavMonth failed", err);
      // Inform the user (simple alert for now) — keep modal open so user can retry
      try {
        alert(
          "Error creando mes en el servidor: " + (err?.message || String(err))
        );
      } catch (e) {
        // fallback: do nothing if alert isn't available
      }
    } finally {
      setCreateLoading(false);
    }
  };

  // Fetch months from server and set state (sorted by period_month asc)
  const fetchNavMonths = async () => {
    setLoading(true);
    try {
      const rows = await db.getNavMonths();
      if (!rows || (Array.isArray(rows) && rows.length === 0)) {
        setMeses(initialMeses);
        return;
      }

      const mapped: MesRendimiento[] = (rows as any[]).map((r) => ({
        id: r.id,
        period_month: r.period_start || r.period_month || null || "",
        value: Number(r.value) || 0,
        total_acquisition_cost: Number(r.total_acquisition_cost) || 0,
        total_asset_value: Number(r.total_asset_value) || 0,
        annual_rents: Number(r.annual_rents) || 0,
        cash: Number(r.cash) || 0,
        equivalents: Number(r.equivalents) || 0,
        mortgages: Number(r.mortgages) || 0,
        bullet_loans: Number(r.bullet_loans) || 0,
        cash_on_cash: Number(r.cash_on_cash) || 0,
        roi: Number(r.roi) || 0,
        created_at: r.created_at,
        updated_at: r.updated_at,
      }));

      // sort ascending by period_month
      mapped.sort((a, b) =>
        a.period_month > b.period_month
          ? 1
          : a.period_month < b.period_month
          ? -1
          : 0
      );
      setMeses(mapped);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("fetchNavMonths failed", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditMes = () => {
    if (editingMes) {
      const tac = parseFloat(editMes.total_acquisition_cost) || 0;
      const tav = parseFloat(editMes.total_asset_value) || 0;
      const annualRents = parseFloat(editMes.annual_rents) || 0;
      const cash = parseFloat(editMes.cash) || 0;
      const equivalents = parseFloat(editMes.equivalents) || 0;
      const mortgages = parseFloat(editMes.mortgages) || 0;
      const bulletLoans = parseFloat(editMes.bullet_loans) || 0;

      const calculatedEditValue =
        (tac + tav) / 2 +
        annualRents +
        cash +
        equivalents -
        mortgages -
        bulletLoans;

      // Try to persist change to server via RPC
      setEditLoading(true);
      (async () => {
        const payload = {
          id: editingMes.id,
          total_acquisition_cost: tac,
          total_asset_value: tav,
          annual_rents: annualRents,
          cash,
          equivalents,
          mortgages,
          bullet_loans: bulletLoans,
          cash_on_cash: parseFloat(editMes.cash_on_cash) || 0,
          roi: parseFloat(editMes.roi) || 0,
          value: calculatedEditValue,
        };

        try {
          await db.updateNavMonth(payload as any);

          // refresh list from server to keep UI consistent
          await fetchNavMonths();
          setIsEditModalOpen(false);
          setEditingMes(null);
          setEditMes({
            total_acquisition_cost: "",
            total_asset_value: "",
            annual_rents: "",
            cash: "",
            equivalents: "",
            mortgages: "",
            bullet_loans: "",
            cash_on_cash: "",
            roi: "",
          });
        } catch (err: any) {
          // Do NOT perform local-only update. Surface error and keep modal open for retry
          // eslint-disable-next-line no-console
          console.error("updateNavMonth failed", err);
          try {
            alert(
              "Error actualizando mes en el servidor: " +
                (err?.message || String(err))
            );
          } catch (e) {
            // ignore
          }
        } finally {
          setEditLoading(false);
        }
      })();
    }
  };

  // Load months on mount and subscribe to realtime changes so UI stays in sync
  useEffect(() => {
    fetchNavMonths();

    // subscribe to changes on nav_general
    const channel = supabase
      .channel("nav_general_changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "nav_general" },
        () => {
          // simple strategy: refetch when there is any change
          fetchNavMonths();
        }
      )
      .subscribe();

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch (e) {
        // ignore
      }
    };
  }, []);

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-black mb-2" style={{ fontWeight: 600 }}>
            Rendimiento
          </h1>
          <p className="text-gray-600">Evolución del portfolio por meses</p>
        </div>
        <Button variant="black" onClick={() => setIsCreateModalOpen(true)}>
          <Plus size={18} className="mr-2" />
          Añadir mes
        </Button>
      </div>

      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
            Filtros
          </Button>
          <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
            Exportar
          </Button> */}
        </div>
      </div>

      {/* Evolution Chart */}
      <EvolutionChart meses={meses} />

      {/* Performance Table */}
      <Card className="border border-gray-200 shadow-sm w-full">
        <div className="overflow-x-auto w-full">
          <div className="overflow-x-auto w-full rounded-lg bg-white">
            <table className="w-full table-auto divide-y divide-gray-200">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th
                  className="text-left px-2 md:px-4 py-3 md:py-4 text-xs text-gray-600 uppercase whitespace-nowrap"
                  style={{ fontWeight: 600, width: "10%" }}
                >
                  Mes
                </th>
                <th
                  className="text-left px-2 md:px-4 py-3 md:py-4 text-xs text-gray-600 uppercase whitespace-nowrap"
                  style={{ fontWeight: 600, width: "10%" }}
                >
                  Value
                </th>
                <th
                  className="hidden md:table-cell text-left px-4 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600, width: "11%" }}
                >
                  Total Acquisition Cost
                </th>
                <th
                  className="hidden md:table-cell text-left px-4 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600, width: "11%" }}
                >
                  Total Asset Value
                </th>
                <th
                  className="hidden md:table-cell text-left px-4 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600, width: "11%" }}
                >
                  90% Annual Rents
                </th>
                <th
                  className="hidden md:table-cell text-left px-4 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600, width: "10%" }}
                >
                  Cash
                </th>
                <th
                  className="hidden md:table-cell text-left px-4 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600, width: "11%" }}
                >
                  Equivalents
                </th>
                <th
                  className="hidden md:table-cell text-left px-4 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600, width: "11%" }}
                >
                  Mortgages
                </th>
                <th
                  className="hidden md:table-cell text-left px-4 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600, width: "15%" }}
                >
                  Bullet Loans
                </th>
                <th className="hidden md:table-cell text-left px-4 py-4 text-xs text-gray-600 uppercase" style={{ fontWeight: 600, width: "8%" }}>
                  CoC (%)
                </th>
                <th className="hidden md:table-cell text-left px-4 py-4 text-xs text-gray-600 uppercase" style={{ fontWeight: 600, width: "8%" }}>
                  ROI (%)
                </th>
                <th
                  className="text-center px-1 md:px-4 py-3 md:py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600, width: "56px" }}
                >
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading
                ? Array.from({ length: 10 }).map((_, i) => (
                    <tr key={`skeleton-${i}`} className="animate-pulse">
                      <td className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded w-24" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded w-20" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded w-28" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded w-28" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded w-24" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded w-20" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded w-24" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded w-24" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded w-28" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-6 bg-gray-200 rounded w-16 mx-auto" />
                      </td>
                    </tr>
                  ))
                : // Keep `meses` state chronological for charts; for the table show most recent first
                  meses
                    .slice()
                    .sort((a, b) => (b.period_month || "").localeCompare(a.period_month || ""))
                    .slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize)
                    .map((mes) => (
                    <tr
                      key={mes.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-2 md:px-4 py-3 md:py-4 whitespace-nowrap truncate max-w-[140px]">
                        <div
                          className="text-sm text-[#0F172A]"
                          style={{ fontWeight: 500 }}
                        >
                          {formatPeriodMonth(mes.period_month)}
                        </div>
                      </td>
                      <td className="px-2 md:px-4 py-3 md:py-4 whitespace-nowrap truncate max-w-[120px] text-right md:text-left">
                        <div className="text-sm text-gray-900 truncate">
                          {mes.value > 0 ? formatCurrency(mes.value) : "-"}
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-4 py-4">
                        <div className="text-sm text-gray-900 truncate">
                          {mes.total_acquisition_cost > 0
                            ? formatCurrency(mes.total_acquisition_cost)
                            : "-"}
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-4 py-4">
                        <div className="text-sm text-gray-900 truncate">
                          {mes.total_asset_value > 0
                            ? formatCurrency(mes.total_asset_value)
                            : "-"}
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-4 py-4">
                        <div className="text-sm text-gray-900 truncate">
                          {mes.annual_rents > 0
                            ? mes.annual_rents.toLocaleString("es-ES")
                            : "-"}
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-4 py-4">
                        <div className="text-sm text-gray-900 truncate">
                          {mes.cash > 0 ? formatCurrency(mes.cash) : "-"}
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-4 py-4">
                        <div className="text-sm text-gray-900 truncate">
                          {mes.equivalents > 0
                            ? formatCurrency(mes.equivalents)
                            : "-"}
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-4 py-4">
                        <div className="text-sm text-gray-900 truncate">
                          {mes.mortgages > 0
                            ? formatCurrency(mes.mortgages)
                            : "-"}
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-4 py-4">
                        <div className="text-sm text-gray-900 truncate">
                          {mes.bullet_loans > 0
                            ? formatCurrency(mes.bullet_loans)
                            : "-"}
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-4 py-4">
                        <div className="text-sm text-gray-900 truncate">{typeof mes.cash_on_cash === 'number' ? `${mes.cash_on_cash.toFixed(2)}%` : "-"}</div>
                      </td>
                      <td className="hidden md:table-cell px-4 py-4">
                        <div className="text-sm text-gray-900 truncate">{typeof mes.roi === 'number' ? `${mes.roi.toFixed(2)}%` : "-"}</div>
                      </td>
                      <td className="px-1 py-3 md:px-4 md:py-4" style={{ width: "56px" }}>
                        <div className="flex items-center justify-center">
                          <Pencil
                            size={20}
                            className="text-gray-500 cursor-pointer p-1 rounded hover:bg-gray-100"
                            onClick={() => {
                              setIsEditModalOpen(true);
                              setEditingMes(mes);
                              setEditMes({
                                total_acquisition_cost:
                                  mes.total_acquisition_cost.toString(),
                                total_asset_value:
                                  mes.total_asset_value.toString(),
                                annual_rents: mes.annual_rents.toString(),
                                cash: mes.cash.toString(),
                                equivalents: mes.equivalents.toString(),
                                mortgages: mes.mortgages.toString(),
                                bullet_loans: mes.bullet_loans.toString(),
                                cash_on_cash: (mes.cash_on_cash ?? 0).toString(),
                                roi: (mes.roi ?? 0).toString(),
                              });
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
            </tbody>
            </table>
          </div>
        </div>
      </Card>

      <div className="flex justify-end mt-4">
        <TablePagination
          page={page}
          totalPages={Math.max(1, Math.ceil(meses.length / pageSize))}
          pageSize={pageSize}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(Math.max(1, Math.ceil(meses.length / pageSize)), p + 1))}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
        />
      </div>

      {/* Create Month Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto text-gray-900 border border-gray-200 shadow-lg" style={{ backgroundColor: '#fafafa' }}>
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontWeight: 600 }}>
              Añadir nuevo mes
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Mes</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="periodMonth"
                type="month"
                value={
                  (newMes.period_month && newMes.period_month.slice(0, 7)) ||
                  nextPeriod.slice(0, 7)
                }
                onChange={(e) =>
                  setNewMes({ ...newMes, period_month: `${e.target.value}-01` })
                }
                disabled={createLoading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Elige el mes que deseas añadir
              </p>
            </div>

            <div className="col-span-2">
              <Label>Value (€)</Label>
              <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-600">
                {formatCurrency(calculatedValue)}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Valor calculado automáticamente
              </p>
            </div>

            <div>
              <Label>Total Acquisition Cost (€)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="totalAcquisitionCost"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={newMes.total_acquisition_cost}
                onChange={(e) =>
                  setNewMes({
                    ...newMes,
                    total_acquisition_cost: e.target.value,
                  })
                }
                disabled={createLoading}
              />
            </div>

            <div>
              <Label>Total Asset Value (€)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="totalAssetValue"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={newMes.total_asset_value}
                onChange={(e) =>
                  setNewMes({ ...newMes, total_asset_value: e.target.value })
                }
                disabled={createLoading}
              />
            </div>

            <div>
              <Label>90% Annual Rents</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="annualRents90"
                type="number"
                step="0.01"
                placeholder="0"
                value={newMes.annual_rents}
                onChange={(e) =>
                  setNewMes({ ...newMes, annual_rents: e.target.value })
                }
                disabled={createLoading}
              />
            </div>

            <div>
              <Label>Cash (€)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="cash"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={newMes.cash}
                onChange={(e) => setNewMes({ ...newMes, cash: e.target.value })}
                disabled={createLoading}
              />
            </div>

            <div>
              <Label>Equivalents (€)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="equivalents"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={newMes.equivalents}
                onChange={(e) =>
                  setNewMes({ ...newMes, equivalents: e.target.value })
                }
                disabled={createLoading}
              />
            </div>

            <div>
              <Label>Mortgages (€)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="mortgages"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={newMes.mortgages}
                onChange={(e) =>
                  setNewMes({ ...newMes, mortgages: e.target.value })
                }
                disabled={createLoading}
              />
            </div>

            <div>
              <Label>Bullet Loans (€)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="bulletLoans"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={newMes.bullet_loans}
                onChange={(e) =>
                  setNewMes({ ...newMes, bullet_loans: e.target.value })
                }
                disabled={createLoading}
              />
            </div>
            <div>
              <Label>CoC (%)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="coc"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={newMes.cash_on_cash}
                onChange={(e) =>
                  setNewMes({ ...newMes, cash_on_cash: e.target.value })
                }
                disabled={createLoading}
              />
            </div>

            <div>
              <Label>ROI (%)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="roi"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={newMes.roi}
                onChange={(e) => setNewMes({ ...newMes, roi: e.target.value })}
                disabled={createLoading}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateModalOpen(false)}
              disabled={createLoading}
            >
              Cancelar
            </Button>
            <Button variant="black" onClick={handleCreateMes} disabled={createLoading}>
              {createLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                  Creando...
                </>
              ) : (
                "Crear mes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Month Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto text-gray-900 border border-gray-200 shadow-lg" style={{ backgroundColor: '#fafafa' }}>
            <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontWeight: 600 }}>
              Editar mes
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Mes</Label>
              <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-600">
                {editingMes ? formatPeriodMonth(editingMes.period_month) : ""}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Mes calculado automáticamente
              </p>
            </div>

            <div className="col-span-2">
              <Label>Value (€)</Label>
              <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-600">
                {editingMes
                  ? formatCurrency(
                      ((parseFloat(editMes.total_acquisition_cost) || 0) +
                        (parseFloat(editMes.total_asset_value) || 0)) /
                        2 +
                        (parseFloat(editMes.annual_rents) || 0) +
                        (parseFloat(editMes.cash) || 0) +
                        (parseFloat(editMes.equivalents) || 0) -
                        (parseFloat(editMes.mortgages) || 0) -
                        (parseFloat(editMes.bullet_loans) || 0)
                    )
                  : ""}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Valor calculado automáticamente
              </p>
            </div>

            <div>
              <Label>Total Acquisition Cost (€)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="totalAcquisitionCost"
                type="number"
                step="0.01"
                placeholder="0.00"
                  value={editMes.total_acquisition_cost}
                  onChange={(e) =>
                    setEditMes({
                      ...editMes,
                      total_acquisition_cost: e.target.value,
                    })
                  }
                  disabled={editLoading}
              />
            </div>

            <div>
              <Label>Total Asset Value (€)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="totalAssetValue"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={editMes.total_asset_value}
                onChange={(e) =>
                  setEditMes({ ...editMes, total_asset_value: e.target.value })
                }
                disabled={editLoading}
              />
            </div>

            <div>
              <Label>90% Annual Rents</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="annualRents90"
                type="number"
                step="0.01"
                placeholder="0"
                value={editMes.annual_rents}
                onChange={(e) =>
                  setEditMes({ ...editMes, annual_rents: e.target.value })
                }
                disabled={editLoading}
              />
            </div>

            <div>
              <Label>Cash (€)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="cash"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={editMes.cash}
                onChange={(e) =>
                  setEditMes({ ...editMes, cash: e.target.value })
                }
                disabled={editLoading}
              />
            </div>

            <div>
              <Label>Equivalents (€)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="equivalents"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={editMes.equivalents}
                onChange={(e) =>
                  setEditMes({ ...editMes, equivalents: e.target.value })
                }
                disabled={editLoading}
              />
            </div>

            <div>
              <Label>Mortgages (€)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="mortgages"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={editMes.mortgages}
                onChange={(e) =>
                  setEditMes({ ...editMes, mortgages: e.target.value })
                }
                disabled={editLoading}
              />
            </div>

            <div>
              <Label>Bullet Loans (€)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="bulletLoans"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={editMes.bullet_loans}
                onChange={(e) =>
                  setEditMes({ ...editMes, bullet_loans: e.target.value })
                }
                disabled={editLoading}
              />
            </div>
            <div>
              <Label>CoC (%)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="editCoc"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={editMes.cash_on_cash}
                onChange={(e) =>
                  setEditMes({ ...editMes, cash_on_cash: e.target.value })
                }
                disabled={editLoading}
              />
            </div>

            <div>
              <Label>ROI (%)</Label>
              <Input
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                id="editRoi"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={editMes.roi}
                onChange={(e) => setEditMes({ ...editMes, roi: e.target.value })}
                disabled={editLoading}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)} disabled={editLoading}>
              Cancelar
            </Button>
            <Button variant="black" onClick={handleEditMes} disabled={editLoading}>
              {editLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                  Guardando...
                </>
              ) : (
                "Guardar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
